/**
 * The live channel (2026-10-21; the plan in docs/live-centrifugo-render.md): one WebSocket to our own Centrifugo
 * for a signed-in person, joined by the server to their personal channel. What arrives is never content — only
 * which store changed (`what`), and the client reads that store again: `chats` → the Chats list (chats-store),
 * `letters` → the letters (use-knocks), `ember` → an Ember's page (OwnerEmberDetail). No poll runs for any of
 * these any more. Rooms' own lines stay on Stream (room-stream.ts).
 *
 * The token comes from POST /api/me/live-token (the url too); the SDK asks again as it runs out, reconnects on
 * its own, and resumes the personal channel from its last offset (recovery), so a short drop loses nothing.
 * After a longer one — or when the tab comes back — every store is read once, in case. 503 (the channel off):
 * nothing connects; the stores still read on open and on return, as before the channel existed.
 */
import { useEffect } from 'react';
import type { Centrifuge, Subscription } from 'centrifuge';
import { emberApi } from './api';
import { ApiError } from '../auth/api-client';
import { trackFailed } from '../../../../shared-web/track';
import { chatsChanged } from './chats-store';
import { reloadClientConfig } from './client-config';
import { notifyKnocksChanged } from './components/use-knocks';

/** A live event, as the edge publishes it (live.js). */
export interface LiveEvent {
  event?: string;
  what: string[];
  type?: string;
  id?: string;
  at?: string;
}

/** The page-level event an Ember's page listens to (detail: the LiveEvent). */
export const LIVE_EMBER_EVENT = 'ember:live-ember';
/** The page-level event the Groups pages listen to (2026-11-03, edge peer-groups.js `groups`): a group of theirs changed. */
export const LIVE_GROUPS_EVENT = 'ember:live-groups';

/** What one event does, as plain calls — the hook wires the real stores, tests pass their own. */
export function applyLiveEvent(data: unknown, acts: { chats: () => void; letters: () => void; ember: (ev: LiveEvent) => void; config?: (rev: string) => void; groups?: () => void }): string[] {
  const ev = data as LiveEvent | null;
  const what = Array.isArray(ev?.what) ? ev.what.filter((w): w is string => typeof w === 'string') : [];
  if (what.includes('chats')) acts.chats();
  if (what.includes('letters')) acts.letters();
  if (what.includes('ember')) acts.ember(ev as LiveEvent);
  if (what.includes('groups')) acts.groups?.();
  // "The config changed" (2026-11-01, app-config — every connection is joined to it): read it again
  if (what.includes('config')) {
    const rev = (data as { rev?: unknown } | null)?.rev;
    acts.config?.(typeof rev === 'string' ? rev : '');
  }
  return what;
}

const REAL = {
  chats: (): void => chatsChanged(),
  letters: (): void => notifyKnocksChanged(),
  ember: (ev: LiveEvent): void => { window.dispatchEvent(new CustomEvent(LIVE_EMBER_EVENT, { detail: ev })); },
  config: (rev: string): void => { void reloadClientConfig(rev || undefined); },
  groups: (): void => { window.dispatchEvent(new Event(LIVE_GROUPS_EVENT)); },
};

/** Every store read once: the catch-up after a reconnect or a return to the tab. */
function readEverything(): void {
  REAL.chats();
  REAL.letters();
  REAL.config('');   // a "config changed" said while away is not replayed to a new connection's token: read it
}

let client: Centrifuge | null = null;
/** Resolves with the client once it exists (the token fetched, the SDK loaded) — or null when it will not. */
let clientReady: Promise<Centrifuge | null> = Promise.resolve(null);
let wanted = false;
/** Bumped by every stop: a start still on its way when the person changed (signed in or out in place) lets go. */
let generation = 0;
let starting = false;
let everConnected = false;
/** Set by a restart: the first connect after it reads the stores once, as a reconnect would. */
let readOnConnect = false;
let lastTry = 0;
const RETRY_AFTER_MS = 60_000;

/** error_detail for ui.failed live_connect: the HTTP status and the edge's code, else the error's words, cut to 120 chars. */
function liveDetail(err: unknown): string {
  if (err instanceof ApiError) return `HTTP ${err.status} ${String((err.body as { error?: unknown } | null)?.error ?? '')}`.trim();
  return String(err instanceof Error ? `${err.name}: ${err.message}` : err).slice(0, 120);
}
/**
 * The token door answered other than 503 or 401 (503 is the channel off, 401 a guest — both expected, not recorded)
 * — 2026-10-27 prod audit.
 */
function doorFailed(err: unknown, step: 'connection' | 'conversation', conversationId?: string): void {
  if (err instanceof ApiError && (err.status === 503 || err.status === 401)) return;
  trackFailed('live_connect', 'door_failed', { step, error_detail: liveDetail(err), ...(conversationId ? { conversation_id: conversationId } : {}) });
}

/**
 * The server refused a token (2026-10-23 prod audit): the connection's subject and a subscription token's subject
 * disagreed — a guest's visitor cookie had changed under an open page — and Centrifugo closed the connection.
 * The SDK reconnects with the token it has, is refused again, and so on every 1.5 s (192 times in five minutes on
 * live-prod). A refused token means the identity moved: the only way back is a fresh connection token, so the
 * client is restarted (new token, the conversations joined again with new tokens), once a minute at most.
 */
export function tokenRefused(code: number | undefined, reason: string | undefined): boolean {
  if (code === 3500) return true;   // Centrifugo DisconnectInvalidToken
  return /token|permission denied|unauthori[sz]ed/i.test(String(reason || ''));
}

/** Server-side drops in a row (code ≥ 3000, not ours): the Nth within the window means the server keeps refusing us. */
export class ServerDrops {
  private at: number[] = [];
  constructor(private readonly limit = 3, private readonly windowMs = 30_000) {}
  /** Records a drop; true when this one is the limit-th inside the window. */
  note(code: number | undefined, now = Date.now()): boolean {
    if (!(typeof code === 'number' && code >= 3000)) return false;
    this.at = this.at.filter((t) => now - t < this.windowMs);
    this.at.push(now);
    return this.at.length >= this.limit;
  }
  reset(): void { this.at = []; }
}

let lastTokenRestart = 0;
function restartForToken(): void {
  if (Date.now() - lastTokenRestart < RETRY_AFTER_MS) return;
  lastTokenRestart = Date.now();
  stop();
  wanted = true;
  lastTry = 0;
  readOnConnect = true;
  void start();
}

async function start(): Promise<void> {
  if (client || starting || !wanted) return;
  if (Date.now() - lastTry < RETRY_AFTER_MS) return;
  const mine = generation;
  starting = true;
  lastTry = Date.now();
  let resolveReady: (c: Centrifuge | null) => void = () => {};
  clientReady = new Promise((res) => { resolveReady = res; });
  try {
    const door = await emberApi.liveToken();   // 503 live_unavailable throws: nothing connects, the stores read on open and on return
    if (!wanted || mine !== generation) return;
    const { Centrifuge: Client } = await import('centrifuge');
    if (!wanted || mine !== generation) return;
    const c = new Client(door.url, {
      token: door.token,
      getToken: async () => {
        try { return (await emberApi.liveToken()).token; } catch (err) {
          trackFailed('live_connect', 'token_refresh_failed', { step: 'connection', error_detail: liveDetail(err) });
          throw err;
        }
      },
    });
    const drops = new ServerDrops();
    c.on('publication', (ctx) => { applyLiveEvent(ctx.data, REAL); });
    c.on('connected', () => {
      // Back after a drop: the SDK replayed what the channel kept; anything older than that is read now
      if (everConnected || readOnConnect) readEverything();
      everConnected = true;
      readOnConnect = false;
    });
    // A server-side close: refused outright, or over and over — either way the tokens are stale (see tokenRefused)
    // Recorded (ui.failed live_connect, 2026-10-27 prod audit: a guest's connection was refused once a second for a whole
    // telling, 5,639 times on live-prod in a day, and no client said so): token_refused, and reconnect_loop on the third
    // server-side close inside 30 s.
    const dropped = (ctx: { code?: number; reason?: string }): void => {
      if (client !== c) return;
      const refused = tokenRefused(ctx.code, ctx.reason);
      const looping = drops.note(ctx.code);
      if (refused) trackFailed('live_connect', 'token_refused', { error_detail: `${ctx.code ?? ''} ${ctx.reason ?? ''}`.trim().slice(0, 120) });
      if (looping) trackFailed('live_connect', 'reconnect_loop', { error_detail: `${ctx.code ?? ''} ${ctx.reason ?? ''}`.trim().slice(0, 120) });
      if (refused || looping) restartForToken();
    };
    c.on('connecting', dropped);
    c.on('disconnected', dropped);
    client = c;
    c.connect();
    // The conversations joined before this client existed, or on the one it replaces, join this one
    for (const e of convEntries) void joinConversation(e, c);
  } catch (err) {
    doorFailed(err, 'connection');
    // the channel is off, a guest (401 since 2026-10-27) or unreachable: nothing connects; the stores read on open and
    // on return, and a signed-in person's next return to the tab tries again in a minute
  } finally {
    if (mine === generation) starting = false;
    resolveReady(mine === generation ? client : null);
  }
}

/** A conversation's channel (phase 3), joined while its page is open. */
export interface LiveConversation {
  /** The channel is joined (events are arriving; a send may skip the stream on the POST). */
  readonly active: boolean;
  close(): void;
}

/**
 * Join `conv:<id>` with the token the edge signs for it (POST /api/conversations/:id/live-token); `onEvent` gets
 * every publication (`what`: turn / reply / progress), `onSubscribed` fires on each (re)join with whether the SDK
 * recovered what was missed — when it did not, the page reads the conversation again. No client (the channel off,
 * a guest refused, the token 503): `active` stays false and nothing is joined — the page keeps the stream on the
 * POST and the Result page its own stream, the fallbacks the edge logs.
 */
interface ConversationHandlers {
  onEvent: (ev: LiveEvent) => void;
  onSubscribed?: (recovered: boolean) => void;
  onError?: (why: string) => void;
}
/** A conversation a page is in: joined on whichever client is current, joined again when the client is replaced. */
interface ConvEntry { id: string; handlers: ConversationHandlers; sub: Subscription | null; joining: boolean; closed: boolean; active: boolean }
const convEntries = new Set<ConvEntry>();

async function joinConversation(e: ConvEntry, c: Centrifuge): Promise<void> {
  if (e.closed || e.sub || e.joining) return;
  e.joining = true;
  let door: { channel: string; token: string };
  try { door = await emberApi.conversationLiveToken(e.id); } catch (err) {
    e.joining = false;
    doorFailed(err, 'conversation', e.id);
    e.handlers.onError?.(err instanceof Error ? err.message : 'token');
    return;
  }
  e.joining = false;
  // Closed meanwhile, the client replaced while the token was on its way (the new client joins again), or the tab
  // went out of sight (it joins again on its return)
  if (e.closed || client !== c || document.visibilityState === 'hidden') return;
  const s = c.newSubscription(door.channel, {
    token: door.token,
    getToken: async () => {
      try { return (await emberApi.conversationLiveToken(e.id)).token; } catch (err) {
        trackFailed('live_connect', 'token_refresh_failed', { step: 'conversation', conversation_id: e.id, error_detail: liveDetail(err) });
        throw err;
      }
    },
  });
  s.on('publication', (ctx) => { if (!e.closed) e.handlers.onEvent(ctx.data as LiveEvent); });
  s.on('subscribed', (ctx) => { e.active = true; if (!e.closed) e.handlers.onSubscribed?.(Boolean(ctx.recovered)); });
  s.on('unsubscribed', () => { e.active = false; });
  s.on('error', (ctx) => {
    e.handlers.onError?.(String(ctx.error?.message || ctx.type || 'subscription'));
    const refused = tokenRefused(ctx.error?.code, ctx.error?.message);
    trackFailed('live_connect', refused ? 'token_refused' : 'subscribe_failed', {
      conversation_id: e.id, error_detail: `${ctx.error?.code ?? ''} ${ctx.error?.message ?? ctx.type ?? ''}`.trim().slice(0, 120),
    });
    if (refused) restartForToken();
  });
  e.sub = s;
  s.subscribe();
}

export function subscribeConversation(conversationId: string, handlers: ConversationHandlers): LiveConversation {
  const e: ConvEntry = { id: conversationId, handlers, sub: null, joining: false, closed: false, active: false };
  convEntries.add(e);
  void clientReady.then((c) => { if (c && client === c) void joinConversation(e, c); });
  return {
    get active() { return e.active; },
    close() {
      e.closed = true; e.active = false;
      convEntries.delete(e);
      const s = e.sub; e.sub = null;
      if (s) { s.unsubscribe(); client?.removeSubscription(s); }
    },
  };
}

function stop(): void {
  wanted = false;
  everConnected = false;
  generation += 1;
  starting = false;
  // A page that joins after this waits on the next start's client, never on the one disconnected here.
  clientReady = Promise.resolve(null);
  const c = client;
  client = null;
  // The conversations' subscriptions go with the client; the entries stay and join the next one
  for (const e of convEntries) { e.sub = null; e.joining = false; e.active = false; }
  if (c) c.disconnect();
}

/** A tab out of sight leaves its conversations (2026-10-26): no one is reading them there, so Ember's words go to the phone. */
function leaveConversations(): void {
  for (const e of convEntries) {
    const s = e.sub; e.sub = null; e.active = false;
    if (s) { s.unsubscribe(); client?.removeSubscription(s); }
  }
}

function onVisible(): void {
  if (!wanted) return;
  if (document.visibilityState !== 'visible') { leaveConversations(); return; }
  if (!client) { void start(); return; }
  // Back in the tab: join the conversations again (a join that recovers nothing reads its conversation again)
  const c = client;
  for (const e of convEntries) void joinConversation(e, c);
  // The tab was away: whatever the channel replays is in the stores already; read them once in case it was longer
  readEverything();
}

/**
 * Keep the live channel open while `enabled`, as `who` (2026-10-22 release check): the connection's subject is
 * whoever asked for its token, so a guest who signs in in place (no page load: the in-app browsers) or a person
 * who signs out is a new connection — on the old one the conversation's token, signed for the new subject, was
 * refused, and the personal channel was the guest's until the token ran out an hour later.
 */
export function useLive(enabled: boolean, who = ''): void {
  useEffect(() => {
    if (!enabled) return undefined;
    wanted = true;
    lastTry = 0;
    void start();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      stop();
    };
  }, [enabled, who]);
}
